import {
  DocumentData,
  QueryDocumentSnapshot,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import Subscription from '../../models/Subscription';
import SMS from '../../models/SMS';
import SMSWithContact from '../../models/@aggregates/SMSWithContact';
import Deliverability from '../../enums/@shared/Deliverability';
import ContactSource from '../../enums/contact/ContactSource';
import ContactLastActivityRole from '../../enums/contact/contact_last_activity/ContactLastActivityRole';
import ContactLastActivityType from '../../enums/contact/contact_last_activity/ContactLastActivityType';
import ContactQualificationAiQualification from '../../enums/contact/contact_qualification_ai/ContactQualificationAiQualification';
import { IContact } from '@leedpulse-monorepo/interfaces';

async function getSMSWithContact(sms: SMS): Promise<SMSWithContact> {
  try {
    // If idContact is empty, create an "unknown" contact
    if (!sms.idContact || sms.idContact.trim() === '') {
      const unknownContact = createUnknownContact(sms.idApiKey);
      return new SMSWithContact(sms, unknownContact);
    }

    const db = firestore;
    const contactDoc = await getDoc(
      doc(db, FirestoreCollections.contacts, sms.idContact)
    );
    if (!contactDoc.exists()) {
      // If the contact doesn't exist, create an "unknown" contact
      const unknownContact = createUnknownContact(sms.idApiKey);
      return new SMSWithContact(sms, unknownContact);
    }
    return new SMSWithContact(sms, contactDoc.data() as IContact);
  } catch (e) {
    console.log('error: ', e);
    // In case of error, create an "unknown" contact
    const unknownContact = createUnknownContact(sms.idApiKey);
    return new SMSWithContact(sms, unknownContact);
  }
}

async function getAllSMS(
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  scopedSubscriptionId?: string | null
): Promise<{
  allSMS: SMSWithContact[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
}> {
  try {
    const db = firestore;
    const allSMSCollection = collection(db, FirestoreCollections.sms);
    if (scopedSubscriptionId) {
      // We first get the corresponding subscription
      const subscriptionDoc = await getDoc(
        doc(db, FirestoreCollections.subscriptions, scopedSubscriptionId)
      );
      if (!subscriptionDoc.exists()) {
        return {
          allSMS: [],
          lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
          totalCollectionCount: 0,
        };
      }
      const subscription = Subscription.fromFirestore(subscriptionDoc);

      // Then we get the allSMS
      const results = lastVisible
        ? await getDocs(
            query(
              allSMSCollection,
              where('id_api_key', '==', subscription.apiKey),
              orderBy('created', 'desc'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(
              allSMSCollection,
              where('id_api_key', '==', subscription.apiKey),
              orderBy('created', 'desc'),
              limit(offset)
            )
          );

      const allSMS: SMSWithContact[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getSMSWithContact(SMS.fromFirestore(doc));
        })
      );

      const allSMSSnapshot = await getCountFromServer(
        query(allSMSCollection, where('id_api_key', '==', subscription.apiKey))
      );

      return {
        allSMS,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: allSMSSnapshot.data().count ?? 0,
      };
    } else {
      const results = lastVisible
        ? await getDocs(
            query(
              allSMSCollection,
              orderBy('created', 'desc'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(allSMSCollection, orderBy('created', 'desc'), limit(offset))
          );

      const allSMS: SMSWithContact[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getSMSWithContact(SMS.fromFirestore(doc));
        })
      );

      const allSMSSnapshot = await getCountFromServer(allSMSCollection);

      return {
        allSMS,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: allSMSSnapshot.data().count ?? 0,
      };
    }
  } catch (e) {
    console.log('error: ', e);
    throw new GetSMSsFailure();
  }
}

export class GetSMSsFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

function createUnknownContact(idApiKey: string): IContact {
  const now = new Date();
  const nowTimestamp = now.getTime();

  const contactInfos : Omit<IContact['contact'], 'id_crm' | 'id_crm_contact'> = {
    details: '',
    prospect_category: '',
    email: '',
    civility: '', 
    firstname: 'Contact',
    sender: '', 
    lastname: 'Inconnu',
    owner: '',
    phone: '',
    company: '',
    adresse: '',
    role: '',
    website: '',
  };

  const contactLastActivity : IContact['last_activity'] = {
    message:'', 
    role: ContactLastActivityRole.user, 
    timestamp: nowTimestamp, 
    type:ContactLastActivityType.sms 
  };

  const contactMessagesSettings : IContact['messages_settings'] = {
    ia_pause: false,
  };

  const contactQualificationAi : Omit<IContact['qualification_ai'], 'qualification_criteria_validated' | 'request_data_deletion'> = {
    qualification: ContactQualificationAiQualification.notInterested,
    reply_needed: false,
    report_to_owner_needed: false,
    summary: '',
    timestamp: nowTimestamp,
    stop_immediat: false,
    
  };

  const messagesCounters = {
    backoffice: {
      assistant: {
        read: 0,
        unread: 0,
      },
      user: {
        read: 0,
        unread: 0,
      },
    },
    frontend: {
      assistant: {
        read: 0,
        unread: 0,
      },
      user: {
        read: 0,
        unread: 0,
      },
    },
  } as IContact['messages_counters'];

  return {
    id: '',
    id_doc: '',
    contact: contactInfos as IContact['contact'],
    created: nowTimestamp, // created
    deliverability:Deliverability.na, // deliverability
    has_replied: false, // hasReplied
    id_api_key: idApiKey, // idApiKey
    imported_by: '', // importedBy
    is_favorite: false, // isFavorite
    is_archived: false, // isArchived
    is_favorite_backoffice: false, // isFavoriteBackoffice
    history: [], // history
    records: [],
    last_activity: contactLastActivity,
    messages_settings: contactMessagesSettings,
    qualification_ai: contactQualificationAi as IContact['qualification_ai'],
    source: ContactSource.leedpulse, // source
    updated: nowTimestamp, // updated
    messages_counters: messagesCounters,
    tags: {},
  };
}


export default getAllSMS;